import { EntityType } from '@frontend/common';
import { AsyncComponent, accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import { ProductExtended } from '@frontend/product/types';
import { StockExtendedClient } from '@frontend/stock/api';
import { StockExtended } from '@frontend/stock/types';
import { lazy } from 'react';
import { FormattedMessage } from 'react-intl';

import { DefaultContainer } from '../../../../modal/detail-modal/default-container/default-container';
import { PlatformObjectBadge } from '../../../../modal/platform-object-badge/platform-object-badge.component';
import { useStockDetail } from './stock-detail.controller';

const SpotSelectV2 = lazy(() => import('@frontend/spot/select').then((module) => ({ default: module.SpotSelectV2 })));
export interface StockDetailProps {
    product: ProductExtended;
}

export const StockDetail = (props: StockDetailProps) => {
    const viewProps = useStockDetail(props);

    return (
        <div className='flex flex-col gap-4'>
            <div className='inline-flex items-start justify-start gap-4 self-stretch'>
                <DefaultContainer
                    title={
                        <FormattedMessage
                            id='stock-detail.title'
                            defaultMessage='Stock info'
                        />
                    }
                    content={
                        <div className='inline-flex flex-1 flex-col items-start justify-start gap-0.75'>
                            <div className='text-foreground-default justify-start text-base font-medium'>
                                <FormattedMessage
                                    id='stock-detail.available'
                                    defaultMessage='Available'
                                />
                            </div>
                            <div
                                data-variant='Default'
                                className='bg-border-default inline-flex h-6 w-6 items-center justify-center gap-2.5 rounded px-1'>
                                <div className='text-foreground-default justify-start text-sm font-medium'>
                                    {viewProps.stockList ? viewProps.stockList.count : 0}
                                </div>
                            </div>
                        </div>
                    }
                />
                <DefaultContainer
                    title='distriSPOT'
                    styles={{ container: 'w-64' }}
                    content={
                        <AsyncComponent>
                            <SpotSelectV2
                                hideLabel
                                id='stock-detail-spot-select'
                                key='stock-detail-spot-select'
                                isClearable
                                onChange={viewProps.setSpot}
                            />
                        </AsyncComponent>
                    }
                />
            </div>
            <ReactiveObjectList<StockExtended>
                label={
                    <FormattedMessage
                        id='stock-detail.stock-list.label'
                        defaultMessage='Currently in SLOTs'
                    />
                }
                dispatch={viewProps.dispatch}
                fetch={() =>
                    StockExtendedClient.fetchStocksExtended({ account_id: props.product.account_id, product_id: props.product.id, spot_id: viewProps.spot })
                }
                useStaticListId
                id='stock-detail-stock-list'
                columns={{
                    columns: ['spot', 'slot', 'quantity'],
                    headerOverwrite: new Map([
                        ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                        ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT]
                    ]),
                    customCells: new Map([
                        [
                            'spot',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    {obj.row.original.spot ? (
                                        <PlatformObjectBadge
                                            id={obj.row.original.spot.entity_id}
                                            entityType={obj.row.original.spot.entity_type}
                                            display={obj.row.original.spot.name}
                                            foreign={obj.row.original.spot.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                        />
                                    ) : (
                                        <span className='text-foreground-muted'>-</span>
                                    )}
                                </td>
                            )
                        ],
                        [
                            'slot',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    {obj.row.original.slot ? (
                                        <PlatformObjectBadge
                                            id={obj.row.original.slot.entity_id}
                                            entityType={obj.row.original.slot.entity_type}
                                            display={obj.row.original.slot.name}
                                            parentIds={{ spot_id: obj.row.original.spot_id, module_id: obj.row.original.module_id }}
                                            foreign={obj.row.original.slot.foreign}
                                            accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                        />
                                    ) : (
                                        <span className='text-foreground-muted'>-</span>
                                    )}
                                </td>
                            )
                        ]
                    ])
                }}
                type={EntityType.STOCK}
                tableProps={{ defaultHeight: true, hideFooter: true }}
            />
        </div>
    );
};
